import axios, { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { getModifiers } from 'actions/modifiers'
import useDispatch from 'hooks/useDispatch'
import useStableStringArray from 'hooks/useStableStringArray'
import { buildTranslate } from 'locales'
import { selectModifiers } from 'selectors/modifier'
import { selectPagination } from 'selectors/pagination'
import { ResolvedAxiosAction } from 'utilities/types'

const t = buildTranslate('errors')

type AxiosCancellationError = {
  error?: { data: string }
}

type UseModifiersParams = {
  search_term?: string
  page?: number
  item_ids?: string[]
  option_ids?: string[]
}

type UseModifiersOptions = {
  shouldSkip?: boolean
  preventCancellation?: boolean
  paginationKey?: string
}

export default function useModifiers(
  params: UseModifiersParams = {},
  options: UseModifiersOptions = {}
) {
  const {
    shouldSkip = false,
    preventCancellation = false,
    paginationKey,
  } = options

  // Automatically determine pagination key based on request type
  const effectivePaginationKey =
    paginationKey ||
    (params.option_ids && params.option_ids.length > 0 ? 'selected' : 'all')

  const dispatch = useDispatch()

  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false)
  const [isApiLoading, setIsApiLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Extract values with stable defaults
  const searchTerm = params.search_term ?? ''
  const page = params.page ?? 1
  const itemIds = params.item_ids
  const optionIds = params.option_ids

  // Memoize item_ids and option_ids safely
  const stableItemIds = useStableStringArray(itemIds)
  const stableOptionIds = useStableStringArray(optionIds)

  // Keep track of the current fetch request to cancel it if needed
  const abortControllerRef = useRef<CancelTokenSource | null>(null)
  // Keep track of whether the current request should be protected from cancellation
  const preventCancellationRef = useRef<boolean>(false)

  // Selectors
  const modifiers = useSelector(state => selectModifiers(state))
  const pagination =
    useSelector(state =>
      selectPagination(state, 'modifier', effectivePaginationKey)
    ) ?? undefined

  const handleSuccess = () => {
    setIsApiLoading(false)
  }

  const handleError = (error: unknown) => {
    setError(
      error instanceof Error ? error : new Error(t('error_page_message'))
    )
    setIsApiLoading(false)
  }

  const fetchModifiers = useCallback(async () => {
    const requestCancelledMessage = 'Request was canceled due to new one.'

    // If there's already an existing request and it's not protected from cancellation, cancel it
    if (abortControllerRef.current && !preventCancellationRef.current) {
      abortControllerRef.current.cancel(requestCancelledMessage)
    }

    // Create a new cancel token for the current request
    const cancelTokenSource = axios.CancelToken.source()
    abortControllerRef.current = cancelTokenSource
    // Store whether this request should be protected from cancellation
    preventCancellationRef.current = preventCancellation

    try {
      setIsApiLoading(true)
      const response = (await dispatch(
        getModifiers(
          stableItemIds as string[],
          stableOptionIds as string[],
          searchTerm,
          page,
          cancelTokenSource.token
        )
      )) as ResolvedAxiosAction<ReturnType<typeof getModifiers>> &
        AxiosCancellationError

      // Skip to 'finally' block if request is cancelled
      if (response?.error?.data === requestCancelledMessage) {
        return
      }

      // Handle a valid error response
      if (response?.error) {
        handleError(response?.error)
        return
      }

      handleSuccess()
    } catch (error) {
      handleError(error)
    } finally {
      setInitialLoadCompleted(true)
    }
  }, [
    dispatch,
    stableItemIds,
    stableOptionIds,
    searchTerm,
    page,
    preventCancellation,
  ])

  useEffect(() => {
    if (shouldSkip) {
      setInitialLoadCompleted(true)
      return
    }
    setInitialLoadCompleted(false)
    fetchModifiers()
  }, [fetchModifiers, shouldSkip])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('Component unmounted')
      }
    }
  }, [])

  return {
    modifiers,
    pagination,
    isReady: initialLoadCompleted && !isApiLoading,
    isLoading: isApiLoading,
    fetchModifiers,
    error,
  }
}
